import {ChangeDetectionStrategy, Component, DestroyRef, EventEmitter, inject, Input, Output} from '@angular/core';
import {ReactiveFormsModule} from '@angular/forms';
import {MatAutocomplete, MatAutocompleteModule} from '@angular/material/autocomplete';
import {MxSelector} from '@aviary/store/store.types';
import {alwaysFalse} from '@aviary/store/common/common.selectors';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ControlRef} from '@aviary/forms/fields/form-fields.types';
import {hasNoValue, isDefined} from '@aviary/store/common/typing.helpers';
import {BaseFormFieldComponent} from '@aviary/forms/fields/base-form-field/base-form-field.component';
import {MatInput} from '@angular/material/input';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {SelectPipe} from '@aviary/pipes/select.pipe';
import {NumericInputOnlyDirective} from './numeric-input-only.directive';
import {getExtendedControlFrom} from '@aviary/forms/form.helpers';
import {ExtendedFormGroup} from '@aviary/forms/extensions/extended-form-group/extended-form-group';
import {ExtendedFormControl} from '@aviary/forms/extensions/extended-form-control/extended-form-control';
import {provideControlRefTokenFor} from '@aviary/providers/control-ref.provider';
import {IconButtonComponent} from '@aviary/buttons/icon-button/icon-button.component';

@Component({
  selector: 'mx-number-form-field',
  templateUrl: './number-form-field.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [provideControlRefTokenFor(NumberFormFieldComponent)],
  imports: [
    BaseFormFieldComponent, MatInput, ReactiveFormsModule, MatAutocompleteModule, MatProgressSpinner, SelectPipe,
    NumericInputOnlyDirective, IconButtonComponent
  ]
})
export class NumberFormFieldComponent implements ControlRef {

  @Input() public overrideLabel?: string;
  @Input() public hintLabel = '';
  @Input() public externalLabel = false;
  @Input() public placeholder = '';
  @Input() public postfixText = '';
  @Input() public alignRight = false;
  @Input() public overrideBrowserAutocomplete?: string; // To be used for forcibly "tricking" browsers & pw managers to stop autocompleting the input
  @Input() public integerOnly = false;

  @Input()
  public set includeClearButton(value: boolean) {
    this._includeClearButton = value;

    this.updateClearButtonFor(this.control?.value ?? '');
  }

  @Input() public replaceClearButtonWithSpinner: MxSelector<boolean> = alwaysFalse;  // Ignored when `showClearButton` is `false`.
  @Input() public auxiliaryButtonIconClass = '';
  @Input() public autocompleteElement: MatAutocomplete;

  @Input()
  public get auxiliaryButtonIconName(): string {
    return this._auxiliaryButtonIconName;
  }

  public set auxiliaryButtonIconName(auxiliaryButtonIconName: string) {
    this._auxiliaryButtonIconName = auxiliaryButtonIconName;
    this.showAuxiliaryButton = !!auxiliaryButtonIconName;
  }

  @Input({required: true})
  public set fieldName(fieldName: string) {
    this._fieldName = fieldName;
    this.initializeControl();
  }

  public get fieldName(): string {
    return this._fieldName;
  }

  @Input({required: true})
  public set formGroup(formGroup: ExtendedFormGroup) {
    this._formGroup = formGroup;
    this.initializeControl();
  }

  public get formGroup(): ExtendedFormGroup {
    return this._formGroup;
  }

  @Input({required: false})
  public set enabled(enabled: boolean) {
    this._enabled = enabled;
    if (isDefined(this.control) && !enabled) this.control.disable();
  }

  public get enabled(): boolean {
    return this._enabled;
  }

  @Output() public readonly keydownEnter: EventEmitter<never> = new EventEmitter<never>();
  @Output() public readonly auxiliaryButtonClick: EventEmitter<never> = new EventEmitter<never>();
  @Output() public readonly inputFocused: EventEmitter<never> = new EventEmitter<never>();
  @Output() public readonly inputBlurred: EventEmitter<never> = new EventEmitter<never>();

  public control: ExtendedFormControl;  // It's tempting to rename this to "formControl", but that's a reserved name and causes confusing errors.  So don't.
  public showClearButton = false;
  public showAuxiliaryButton = false;
  public focused = false;

  private _auxiliaryButtonIconName: string;
  private _includeClearButton: boolean = true;
  private _fieldName: string;
  private _formGroup: ExtendedFormGroup;
  private _enabled = true;
  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  public initializeControl(): void {
    if (hasNoValue(this._formGroup) || hasNoValue(this._fieldName)) return;

    this.control = getExtendedControlFrom(this._formGroup, this._fieldName);
    if (hasNoValue(this.control)) return;

    if (!this._enabled) this.control.disable();

    this.updateClearButtonFor(this.control.value);

    this.control.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => this.updateClearButtonFor(value));
  }

  public onFocusInput(): void {
    this.focused = true;
    this.inputFocused.emit();
  }

  public onBlurInput(): void {
    this.focused = false;

    this.inputBlurred.emit();
  }

  public onKeydownEnter(): void {
    this.keydownEnter.emit();
  }

  public onClickClearButton(): void {
    this.control.reset('');
  }

  public onClickAuxiliaryButton(): void {
    this.auxiliaryButtonClick.emit();
  }

  private updateClearButtonFor(value: unknown): void {
    this.showClearButton = this._includeClearButton && isDefined(value) && String(value).length > 0;
  }
}
